import { baseApi } from "./baseApi";
import { supabase } from "../lib/supabaseClient";

export const profileApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProfile: builder.query<any, { username?: string; userId?: string }>({
      queryFn: async ({ username, userId }) => {
        try {
          let query = supabase.from("user_profiles").select("*");
          if (username) {
            query = query.eq("username", username);
          } else if (userId) {
            query = query.eq("id", userId);
          } else {
            return { error: { status: 400, statusText: "Missing args", data: "Missing args" } };
          }
          const { data, error } = await query.single();
          if (error) throw error;
          return { data };
        } catch (error: any) {
          return { error: { status: 500, statusText: error.message, data: error } };
        }
      },
      providesTags: (result, error, arg) => [{ type: "Profile", id: arg.username || arg.userId }],
    }),
    getProfileFilms: builder.query<any[], { userId: string; status: string }>({
      queryFn: async ({ userId, status }) => {
        try {
          if (status === "currently") {
            const { data, error } = await supabase
              .from("user_current_activities")
              .select("imdb_id, type, films(*)")
              .eq("user_id", userId);
            if (error) throw error;
            return { data: data.map((item: any) => item.films) };
          } else {
            const { data, error } = await supabase
              .from("user_interactions")
              .select("imdb_id, films(*)")
              .eq("user_id", userId)
              .eq("status", status);
            if (error) throw error;
            return { data: data.map((item: any) => item.films) };
          }
        } catch (error: any) {
          return { error: { status: 500, statusText: error.message, data: error } };
        }
      },
      providesTags: (result, error, arg) => [{ type: "Profile", id: `${arg.userId}-films-${arg.status}` }],
    }),
    getFollowStats: builder.query<
      { followers: number; following: number; isFollowing: boolean; followersList?: any[]; followingList?: any[] },
      { userId: string; currentUserId?: string }
    >({
      queryFn: async ({ userId, currentUserId }) => {
        try {
          const [followersRes, followingRes, isFollowingRes] = await Promise.all([
            supabase.from("user_follows").select("*", { count: "exact", head: true }).eq("following_id", userId),
            supabase.from("user_follows").select("*", { count: "exact", head: true }).eq("follower_id", userId),
            currentUserId
              ? supabase
                  .from("user_follows")
                  .select("id")
                  .eq("follower_id", currentUserId)
                  .eq("following_id", userId)
                  .maybeSingle()
              : Promise.resolve({ data: null }),
          ]);

          return {
            data: {
              followers: followersRes.count || 0,
              following: followingRes.count || 0,
              isFollowing: !!isFollowingRes.data,
            },
          };
        } catch (error: any) {
          return { error: { status: 500, statusText: error.message, data: error } };
        }
      },
      providesTags: (result, error, arg) => [{ type: "Follow", id: arg.userId }],
    }),
    getFollowList: builder.query<any[], { userId: string; type: "followers" | "following" }>({
      queryFn: async ({ userId, type }) => {
        try {
          const column = type === "followers" ? "following_id" : "follower_id"; // Filter by target
          const selectCol = type === "followers" ? "follower_id" : "following_id"; // Select the OTHER person

          const { data } = await supabase.from("user_follows").select(selectCol).eq(column, userId);

          if (!data) return { data: [] };

          const ids = data.map((d: any) => d[selectCol]);
          if (ids.length === 0) return { data: [] };

          const { data: profiles, error } = await supabase.from("user_profiles").select("*").in("id", ids);
          if (error) throw error;

          return { data: profiles || [] };
        } catch (error: any) {
          return { error: { status: 500, statusText: error.message, data: error } };
        }
      },
    }),
    toggleFollow: builder.mutation<void, { targetUserId: string; currentUserId: string; isFollowing: boolean }>({
      queryFn: async ({ targetUserId, currentUserId, isFollowing }) => {
        try {
          if (isFollowing) {
            const { error } = await supabase
              .from("user_follows")
              .delete()
              .eq("follower_id", currentUserId)
              .eq("following_id", targetUserId);
            if (error) throw error;
          } else {
            const { error } = await supabase.from("user_follows").insert([
              {
                follower_id: currentUserId,
                following_id: targetUserId,
              },
            ]);
            if (error) throw error;
          }
          return { data: undefined };
        } catch (error: any) {
          return { error: { status: 500, statusText: error.message, data: error } };
        }
      },
      invalidatesTags: (result, error, arg) => [{ type: "Follow", id: arg.targetUserId }],
    }),
    searchUsers: builder.query<any[], { query: string; limit?: number }>({
      queryFn: async ({ query, limit = 5 }) => {
        try {
          if (!query || query.length < 2) return { data: [] };
          const { data, error } = await supabase
            .from("user_profiles")
            .select("*")
            .or(`username.ilike.%${query}%,display_name.ilike.%${query}%`)
            .limit(limit);
          if (error) throw error;
          return { data: data || [] };
        } catch (error: any) {
          return { error: { status: 500, statusText: error.message, data: error } };
        }
      },
    }),
  }),
});

export const {
  useGetProfileQuery,
  useGetProfileFilmsQuery,
  useGetFollowStatsQuery,
  useGetFollowListQuery,
  useLazyGetFollowListQuery,
  useToggleFollowMutation,
  useSearchUsersQuery,
  useLazySearchUsersQuery,
} = profileApi;
